import produce from 'immer';
import { create } from 'zustand';

export type ProjectTeamConfigurationMapping = {
  projectTeamConfigurationId: any;
  projectTeamName: string;
  mappingId: any;
  migrate: boolean;
  messages: [];
};
export type ImportMappingsToMigrate = { name: string; importMappingId: string; migrate: boolean; messages: string[] };

export type ReleaseProcessState =
  | 'idle' // nothing happened
  | 'releaseRequested' // the event is sent to request the release
  | 'validating' // the release is being validated
  | 'updating dependencies' // the release was completed, the frontend is now handling the updating of dependencies
  | 'released' // all done
  | 'validationFailed'; // the validating step failed

export type ReleaseTemplateWizardState = {
  currentTemplateId?: string;
  projectTeamConfigurationMappings: ProjectTeamConfigurationMapping[];
  importMappingsToMigrate: ImportMappingsToMigrate[];
  dependenciesLoaded: boolean;
  isReleaseInProgress: boolean;
  numberOfItemsToMigrate: number;
  numberOfMigratedItems: number;
  setNumberOfItemsToMigrate: (numberOfItemsToMigratE: number) => void;
  setNumberOfMigratedItems: (numberOfMigratedItems: number) => void;
  increaseNumberOfMigratedItems: () => void;

  clear: () => void;
  addMappings: (teamMappings: ProjectTeamConfigurationMapping[]) => void;
  addImport: (importMappings: ImportMappingsToMigrate) => void;

  toggleProjectTeamConfigurationMapping: (projectTeamConfigurationMapping: ProjectTeamConfigurationMapping) => void;
  toggleImportMapping: (importMappingsToMigrate: ImportMappingsToMigrate) => void;

  setIsReleaseInProgress: (isInProgress: boolean) => void;
  setDependenciesLoaded: (dependenciesLoaded: boolean) => void;

  releaseProcessState: ReleaseProcessState;
  releaseFailedMessages: string[];

  setReleaseRequested: (templateId: string) => void;
  setUpdatingDependencies: () => void;
  setReleaseComplete: () => void;
  setValidationResult: (templateId: string, success: boolean, messages: string[]) => void;
};

export const RELEASE_TEMPLATE_WIZARD_STORE = create<ReleaseTemplateWizardState>((set) => ({
  currentTemplateId: undefined,
  projectTeamConfigurationMappings: [],
  importMappingsToMigrate: [],
  isReleaseInProgress: false,
  dependenciesLoaded: false,
  numberOfItemsToMigrate: -1,
  numberOfMigratedItems: -1,

  releaseProcessState: 'idle',
  releaseFailedMessages: [],

  setReleaseRequested: (templateId: string) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        draft.currentTemplateId = templateId;
        draft.releaseProcessState = 'releaseRequested';
        draft.releaseFailedMessages = [];
      });

      return newState;
    }),
  setUpdatingDependencies: () =>
    set((state) => {
      const newState = produce(state, (draft) => {
        draft.releaseProcessState = 'updating dependencies';
        draft.releaseFailedMessages = [];
      });

      return newState;
    }),
  setReleaseComplete: () =>
    set((state) => {
      const newState = produce(state, (draft) => {
        draft.releaseProcessState = 'released';
        draft.releaseFailedMessages = [];
      });

      return newState;
    }),
  setValidationResult: (templateId: string, success: boolean, messages: string[]) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        if (state.currentTemplateId === templateId) {
          draft.releaseProcessState = success ? 'updating dependencies' : 'validationFailed';
          draft.releaseFailedMessages = messages;
        }
      });

      return newState;
    }),

  clear: () =>
    set((state) => {
      const newState = produce(state, (draft) => {
        draft.projectTeamConfigurationMappings = [];
        draft.importMappingsToMigrate = [];
        draft.dependenciesLoaded = false;
        draft.isReleaseInProgress = false;
        draft.numberOfItemsToMigrate = -1;
        draft.numberOfMigratedItems = -1;
        draft.releaseProcessState = 'idle';
        draft.releaseFailedMessages = [];
        draft.currentTemplateId = undefined;
      });

      return newState;
    }),
  addMappings: (teamMappings: ProjectTeamConfigurationMapping[]) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        draft.projectTeamConfigurationMappings = teamMappings;
      });

      return newState;
    }),
  addImport: (importMappings: ImportMappingsToMigrate) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        draft.importMappingsToMigrate.push(importMappings);
      });

      return newState;
    }),
  toggleProjectTeamConfigurationMapping: (projectTeamConfigurationMapping: ProjectTeamConfigurationMapping) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        const index = state.projectTeamConfigurationMappings.findIndex(
          (p) =>
            p.projectTeamConfigurationId === projectTeamConfigurationMapping.projectTeamConfigurationId &&
            p.mappingId === projectTeamConfigurationMapping.mappingId
        );
        const migrate = state.projectTeamConfigurationMappings[index].migrate;
        draft.projectTeamConfigurationMappings[index].migrate = !migrate;
      });

      return newState;
    }),
  toggleImportMapping: (importMappingsToMigrate: ImportMappingsToMigrate) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        const index = state.importMappingsToMigrate.findIndex(
          (p) => p.importMappingId === importMappingsToMigrate.importMappingId
        );
        const migrate = state.importMappingsToMigrate[index].migrate;

        draft.importMappingsToMigrate[index].migrate = !migrate;
      });

      return newState;
    }),
  setIsReleaseInProgress: (isInProgress: boolean) => set({ isReleaseInProgress: isInProgress }),
  setDependenciesLoaded: (dependenciesLoaded: boolean) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        draft.dependenciesLoaded = dependenciesLoaded;
      });

      return newState;
    }),
  setNumberOfItemsToMigrate: (numberOfItemsToMigrate: number) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        draft.numberOfItemsToMigrate = numberOfItemsToMigrate;
      });

      return newState;
    }),
  setNumberOfMigratedItems: (numberOfMigratedItems: number) =>
    set((state) => {
      const newState = produce(state, (draft) => {
        draft.numberOfMigratedItems = numberOfMigratedItems;
      });

      return newState;
    }),
  increaseNumberOfMigratedItems: () =>
    set((state) => {
      const newState = produce(state, (draft) => {
        draft.numberOfMigratedItems = state.numberOfMigratedItems + 1;
      });

      return newState;
    })
}));
